import { useCallback, useMemo } from 'react';
import { ProCard } from '@ant-design/pro-components';
import { Form, Input, Select, Tabs } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import Permission from '@/components/permission';
import AuthorNameIdCell from '@/pages/ugc-reports/components/AuthorNameIdCell';
import MediaTitleIdCell from '@/pages/ugc-reports/components/MediaTitleIdCell';
import ReportTimeFilter from '@/pages/ugc-reports/components/ReportTimeFilter';
import WorkKindCell from '@/pages/ugc-reports/components/WorkKindCell';
import UgcReportListPage from '@/pages/ugc-reports/components/UgcReportListPage';
import {
    REPORT_PERMISSION,
    WORK_DRAMA_REPORT_TYPE_OPTIONS,
    WORK_KIND_OPTIONS,
} from '@/pages/ugc-reports/constants';
import { buildTrailingReportColumns } from '@/pages/ugc-reports/hooks/useTrailingReportColumns';
import {
    formatEpisodeCount,
    getAuthorNickname,
    getAuthorUserId,
    getTargetString,
    getWorkCoverUrl,
    getWorkVideoUrl,
} from '@/pages/ugc-reports/utils';

import type { ProColumns } from '@ant-design/pro-components';
import type { UgcReportTicket } from '@/services/ugc-reports';

const numericInputProps = {
    getValueFromEvent: (e: { target?: { value?: string } }) =>
        String(e?.target?.value ?? '').replace(/\D/g, ''),
};

const useContentTypeOptions = () => {
    const { formatMessage } = useIntl();
    return useMemo(
        () =>
            WORK_DRAMA_REPORT_TYPE_OPTIONS.map((item) => ({
                label: formatMessage({ id: `ugcReports.contentType.${item.code}` }),
                value: item.code,
            })),
        [formatMessage],
    );
};

const useWorkKindOptions = () => {
    const { formatMessage } = useIntl();
    return useMemo(
        () =>
            WORK_KIND_OPTIONS.map((item) => ({
                label: formatMessage({ id: `ugcReports.workKind.${item.value}` }),
                value: item.value,
            })),
        [formatMessage],
    );
};

const WorkReportPanel: React.FC = () => {
    const reportTypeOptions = useContentTypeOptions();
    const workKindOptions = useWorkKindOptions();
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const buildColumns = useCallback(
        ({
            expandedRowKeys,
            toggleExpand,
        }: {
            expandedRowKeys: string[];
            toggleExpand: (record: UgcReportTicket) => void;
        }) => {
            const trailing = buildTrailingReportColumns({
                dictionary: 'content',
                expandedRowKeys,
                toggleExpand,
                navigate,
            });
            return [
                {
                    title: <FormattedMessage id="ugcReports.columns.workInfo" />,
                    width: 320,
                    render: (_: unknown, record: UgcReportTicket) => {
                        const target = record.target;
                        return (
                            <MediaTitleIdCell
                                title={getTargetString(target, 'title')}
                                id={record.target_id}
                                cover={getWorkCoverUrl(target)}
                                videoUrl={getWorkVideoUrl(target)}
                                withBookTitle={false}
                            />
                        );
                    },
                },
                {
                    title: <FormattedMessage id="ugcReports.columns.workKind" />,
                    width: 200,
                    render: (_: unknown, record: UgcReportTicket) => <WorkKindCell record={record} />,
                },
                {
                    title: <FormattedMessage id="ugcReports.columns.author" />,
                    width: 180,
                    render: (_: unknown, record: UgcReportTicket) => (
                        <AuthorNameIdCell
                            name={getAuthorNickname(record)}
                            userId={getAuthorUserId(record)}
                        />
                    ),
                },
                trailing.reasonColumn,
                trailing.timeRangeColumn,
                trailing.reportCountColumn,
                trailing.actionColumn,
            ] as ProColumns<UgcReportTicket>[];
        },
        [formatMessage, navigate],
    );

    const searchFields = (
        <>
            <ReportTimeFilter />
            <Form.Item name="report_type_code" style={{ marginBottom: 0 }}>
                <Select
                    allowClear
                    options={reportTypeOptions}
                    placeholder={formatMessage({ id: 'ugcReports.search.reportType' })}
                    style={{ width: 180 }}
                />
            </Form.Item>
            <Form.Item name="work_kind" style={{ marginBottom: 0 }}>
                <Select
                    allowClear
                    options={workKindOptions}
                    placeholder={formatMessage({ id: 'ugcReports.search.workKind' })}
                    style={{ width: 160 }}
                />
            </Form.Item>
            <Form.Item name="work_id" style={{ marginBottom: 0 }} {...numericInputProps}>
                <Input
                    allowClear
                    inputMode="numeric"
                    placeholder={formatMessage({ id: 'ugcReports.search.workId' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="work_name" style={{ marginBottom: 0 }}>
                <Input
                    allowClear
                    placeholder={formatMessage({ id: 'ugcReports.search.workName' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="author_user_id" style={{ marginBottom: 0 }} {...numericInputProps}>
                <Input
                    allowClear
                    inputMode="numeric"
                    placeholder={formatMessage({ id: 'ugcReports.search.authorId' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="author_name" style={{ marginBottom: 0 }}>
                <Input
                    allowClear
                    placeholder={formatMessage({ id: 'ugcReports.search.authorName' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="reporter_user_id" style={{ marginBottom: 0 }} {...numericInputProps}>
                <Input
                    allowClear
                    inputMode="numeric"
                    placeholder={formatMessage({ id: 'ugcReports.search.reporterId' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="reporter_nickname" style={{ marginBottom: 0 }}>
                <Input
                    allowClear
                    placeholder={formatMessage({ id: 'ugcReports.search.reporterNickname' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
        </>
    );

    return (
        <UgcReportListPage
            targetType="WORK"
            buildColumns={buildColumns}
            searchFields={searchFields}
            dictionary="content"
            scrollX={1540}
            withTimeFilter
        />
    );
};

const DramaReportPanel: React.FC = () => {
    const reportTypeOptions = useContentTypeOptions();
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const buildColumns = useCallback(
        ({
            expandedRowKeys,
            toggleExpand,
        }: {
            expandedRowKeys: string[];
            toggleExpand: (record: UgcReportTicket) => void;
        }) => {
            const trailing = buildTrailingReportColumns({
                dictionary: 'content',
                expandedRowKeys,
                toggleExpand,
                navigate,
                countLabelId: 'ugcReports.columns.dramaReportCount',
            });
            return [
                {
                    title: <FormattedMessage id="ugcReports.columns.dramaInfoIp" />,
                    width: 320,
                    render: (_: unknown, record: UgcReportTicket) => {
                        const target = record.target;
                        const dramaId = record.drama_id || record.target_id;
                        return (
                            <MediaTitleIdCell
                                title={getTargetString(target, 'title')}
                                id={dramaId}
                                cover={getWorkCoverUrl(target)}
                            />
                        );
                    },
                },
                {
                    title: <FormattedMessage id="ugcReports.columns.episodeCount" />,
                    width: 120,
                    render: (_: unknown, record: UgcReportTicket) => {
                        const count = formatEpisodeCount(record.target);
                        return count
                            ? formatMessage({ id: 'ugcReports.columns.totalEpisodes' }, { count })
                            : '-';
                    },
                },
                {
                    title: <FormattedMessage id="ugcReports.columns.producerAuthor" />,
                    width: 180,
                    render: (_: unknown, record: UgcReportTicket) => (
                        <AuthorNameIdCell
                            name={getAuthorNickname(record)}
                            userId={getAuthorUserId(record)}
                        />
                    ),
                },
                trailing.reasonColumn,
                trailing.timeRangeColumn,
                trailing.reportCountColumn,
                trailing.actionColumn,
            ] as ProColumns<UgcReportTicket>[];
        },
        [formatMessage, navigate],
    );

    const searchFields = (
        <>
            <ReportTimeFilter />
            <Form.Item name="report_type_code" style={{ marginBottom: 0 }}>
                <Select
                    allowClear
                    options={reportTypeOptions}
                    placeholder={formatMessage({ id: 'ugcReports.search.reportType' })}
                    style={{ width: 180 }}
                />
            </Form.Item>
            <Form.Item name="drama_id" style={{ marginBottom: 0 }} {...numericInputProps}>
                <Input
                    allowClear
                    inputMode="numeric"
                    placeholder={formatMessage({ id: 'ugcReports.search.dramaId' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="drama_name" style={{ marginBottom: 0 }}>
                <Input
                    allowClear
                    placeholder={formatMessage({ id: 'ugcReports.search.dramaName' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="author_user_id" style={{ marginBottom: 0 }} {...numericInputProps}>
                <Input
                    allowClear
                    inputMode="numeric"
                    placeholder={formatMessage({ id: 'ugcReports.search.authorId' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="author_name" style={{ marginBottom: 0 }}>
                <Input
                    allowClear
                    placeholder={formatMessage({ id: 'ugcReports.search.authorName' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="reporter_user_id" style={{ marginBottom: 0 }} {...numericInputProps}>
                <Input
                    allowClear
                    inputMode="numeric"
                    placeholder={formatMessage({ id: 'ugcReports.search.reporterId' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="reporter_nickname" style={{ marginBottom: 0 }}>
                <Input
                    allowClear
                    placeholder={formatMessage({ id: 'ugcReports.search.reporterNickname' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
        </>
    );

    return (
        <UgcReportListPage
            targetType="DRAMA"
            buildColumns={buildColumns}
            searchFields={searchFields}
            dictionary="content"
            scrollX={1500}
            withTimeFilter
        />
    );
};

const PageUgcContentReports: React.FC = () => {
    const { formatMessage } = useIntl();

    const tabItems = useMemo(
        () => [
            {
                key: 'work',
                label: formatMessage({ id: 'ugcReports.tabs.work' }),
                children: <WorkReportPanel />,
            },
            {
                key: 'drama',
                label: formatMessage({ id: 'ugcReports.tabs.drama' }),
                children: <DramaReportPanel />,
            },
        ],
        [formatMessage],
    );

    return (
        <Permission permission={REPORT_PERMISSION}>
            <ProCard>
                <Tabs items={tabItems} destroyInactiveTabPane />
            </ProCard>
        </Permission>
    );
};

export default PageUgcContentReports;
